import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Table, Input, Select, Button, Alert, message, Space, Modal, Form, InputNumber } from "antd";
import axios from "../../axiosConfig";

export default function Leaderboards() {
  const [leaderboardName, setLeaderboardName] = useState("");
  const [leaderboardList, setLeaderboardList] = useState([]);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inactive, setInactive] = useState(false);
  const [searched, setSearched] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editForm] = Form.useForm();
  const editRecordRef = useRef(null);
  const [addModal, setAddModal] = useState(false);
  const [addForm] = Form.useForm();
  const [extraFields, setExtraFields] = useState([]);

  useEffect(() => {
    axios
      .get("/dashboard/limit/leaderboard-names/")
      .then((res) => {
        setLeaderboardList(res.data.leaderboards || []);
      })
      .catch(() => {
        message.error("Failed to load leaderboard list");
      });
  }, []);

  function loadLeaderboard() {
    const name = leaderboardName.trim();
    if (!name) {
      message.warning("Please enter a leaderboard name");
      return;
    }

    setLoading(true);
    setInactive(false);
    setSearched(true);

    axios
      .get(`/dashboard/limit/leaderboard-full-data/?l_name=${encodeURIComponent(name)}`)
      .then((res) => {
        const { vendors: vendorData, is_active } = res.data;

        if (!is_active) {
          setInactive(true);
          setData([]);
          setColumns([]);
          setLoading(false);
          return;
        }

        const entries = Object.entries(vendorData || {});
        if (entries.length === 0) {
          setData([]);
          setColumns([]);
          setLoading(false);
          return;
        }

        const rows = entries
          .map(([vendorName, values], index) => ({
            key: index,
            vendor_name: vendorName,
            ...values,
          }));

        const extraKeys = new Set();
        rows.forEach((row) => {
          Object.keys(row).forEach((k) => {
            if (!["key", "vendor_name"].includes(k)) {
              extraKeys.add(k);
            }
          });
        });

        const cols = [
          {
            title: "#",
            key: "row_num",
            width: 60,
            render: (_, __, index) => index + 1,
          },
          {
            title: "Vendor Name",
            dataIndex: "vendor_name",
            key: "vendor_name",
            sorter: (a, b) => a.vendor_name.localeCompare(b.vendor_name),
          },
        ];

        const sortedExtra = [...extraKeys].sort();
        const fieldTypes = {};
        sortedExtra.forEach((k) => {
          const sample = rows.find((r) => r[k] != null);
          fieldTypes[k] = sample ? typeof sample[k] : "string";
        });
        setExtraFields(sortedExtra.map((k) => ({ key: k, type: fieldTypes[k] })));
        sortedExtra.forEach((k) => {
          cols.push({
            title: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
            dataIndex: k,
            key: k,
            sorter: (a, b) => {
              const av = a[k], bv = b[k];
              if (typeof av === "number" && typeof bv === "number") return av - bv;
              return String(av || "").localeCompare(String(bv || ""));
            },
            render: (val) => {
              if (val == null) return "—";
              if (typeof val === "number") return val.toLocaleString();
              if (typeof val === "string" && val.match(/^https?:\/\/.+\.(png|jpg|jpeg|gif|svg|webp)/i)) {
                return <img src={val} alt={k} style={{ maxHeight: 32 }} />;
              }
              return String(val);
            },
          });
        });

        cols.push({
          title: "Action",
          key: "action",
          render: (_, record) => (
            <Button size="small" onClick={() => openEditModal(record)}>
              Edit
            </Button>
          ),
        });

        setColumns(cols);
        setData(rows);
        setLoading(false);
      })
      .catch((err) => {
        const msg = err.response?.data?.error || "Failed to load leaderboard";
        message.error(msg);
        setData([]);
        setColumns([]);
        setLoading(false);
      });
  }

  function openEditModal(record) {
    editRecordRef.current = record;
    const fields = {};
    Object.keys(record).forEach((k) => {
      if (!["key", "vendor_name"].includes(k)) {
        fields[k] = record[k];
      }
    });
    editForm.setFieldsValue(fields);
    setEditModal(true);
  }

  function submitEdit() {
    editForm.validateFields().then((values) => {
      const record = editRecordRef.current;
      axios
        .post("/dashboard/limit/leaderboard-data-update/", {
          l_name: leaderboardName.trim(),
          vendor_name: record.vendor_name,
          vendor_data: values,
        })
        .then(() => {
          message.success(`Updated ${record.vendor_name}`);
          setEditModal(false);
          editForm.resetFields();
          loadLeaderboard();
        })
        .catch((err) => {
          const msg = err.response?.data?.error || "Failed to update vendor data";
          message.error(msg);
        });
    });
  }

  function getEditFields() {
    const record = editRecordRef.current;
    if (!record) return [];
    return Object.keys(record).filter((k) => !["key", "vendor_name"].includes(k));
  }

  function submitAddVendor() {
    addForm.validateFields().then((values) => {
      const { vendor_name, ...vendorData } = values;
      axios
        .post("/dashboard/limit/leaderboard-data-update/", {
          l_name: leaderboardName.trim(),
          vendor_name,
          vendor_data: vendorData,
        })
        .then(() => {
          message.success(`Added ${vendor_name}`);
          setAddModal(false);
          addForm.resetFields();
          loadLeaderboard();
        })
        .catch((err) => {
          const msg = err.response?.data?.error || "Failed to add vendor";
          message.error(msg);
        });
    });
  }

  return (
    <div>
      <Row style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Select
              placeholder="Select a leaderboard"
              value={leaderboardName || undefined}
              onChange={(val) => setLeaderboardName(val)}
              style={{ width: 300 }}
              showSearch
              filterOption={(input, option) =>
                option.children.join("").toLowerCase().includes(input.toLowerCase())
              }
            >
              {leaderboardList.map((lb) => (
                <Select.Option key={lb.name} value={lb.name}>
                  {lb.name}{lb.is_active ? "" : " (inactive)"}
                </Select.Option>
              ))}
            </Select>
            <Button type="primary" onClick={loadLeaderboard} loading={loading}>
              Load
            </Button>
          </Space>
        </Col>
      </Row>

      {inactive && (
        <Alert
          message="Leaderboard Inactive"
          description="This leaderboard is currently inactive or was not found."
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {searched && !inactive && (
        <Row style={{ marginBottom: 16 }}>
          <Col>
            <Button type="primary" onClick={() => setAddModal(true)}>
              Add Vendor
            </Button>
          </Col>
        </Row>
      )}

      {searched && !inactive && (
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          locale={{ emptyText: "No data available" }}
          pagination={{
            pageSizeOptions: ["10", "25", "100"],
            showSizeChanger: true,
            defaultPageSize: 10,
          }}
        />
      )}

      <Modal
        visible={editModal}
        onOk={submitEdit}
        onCancel={() => { setEditModal(false); editForm.resetFields(); }}
        title={`Edit — ${editRecordRef.current?.vendor_name || ""}`}
        okText="Save"
      >
        <Form form={editForm} layout="vertical">
          {getEditFields().map((k) => {
            const val = editRecordRef.current?.[k];
            return (
              <Form.Item
                key={k}
                name={k}
                label={k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              >
                {typeof val === "number" ? <InputNumber style={{ width: "100%" }} /> : <Input />}
              </Form.Item>
            );
          })}
        </Form>
      </Modal>

      <Modal
        visible={addModal}
        onOk={submitAddVendor}
        onCancel={() => { setAddModal(false); addForm.resetFields(); }}
        title="Add Vendor"
        okText="Add"
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            name="vendor_name"
            label="Vendor Name"
            rules={[{ required: true, message: "Please enter a vendor name" }]}
          >
            <Input />
          </Form.Item>
          {extraFields.map((f) => (
            <Form.Item
              key={f.key}
              name={f.key}
              label={f.key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            >
              {f.type === "number" ? <InputNumber style={{ width: "100%" }} /> : <Input />}
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </div>
  );
}
